<script lang="ts" setup>
import { computed } from 'vue'

import { statusLabels } from '@/misc/constants'
import {
  formatConfidence,
  formatCost,
  formatGroupingCost
} from '@/stay-list/stay-list-table/helpers/line/lineContentFormatters'
import { ExternalPmsiStatusLabel } from '@/stay-list/stay-list-table/helpers/line/lineHelper'
import useQuickMode from '@/stay-list/stay-list-table/line/composables/quickMode'
import useStayListLine from '@/stay-list/stay-list-table/line/composables/stayListLine'
import useStayRedirection from '@/stay-list/stay-list-table/line/composables/stayRedirection'
import NotificationLineColumn from '@/stay-list/stay-list-table/line/NotificationLineColumn.vue'
import { ListMcoStay } from '@/stay-list/stay-list-table/mco/types'
import { StayListLineProps } from '@/stay-list/stay-list-table/types'
import { useStore } from '@/store'
import { AutomationTypeEnum } from '@/store/modules/automation/types'
import { CountryEnum, ModeEnum } from '@/store/modules/settings/types'
import { SeenStatusEnum } from '@/store/modules/stay/types'

const props = withDefaults(defineProps<StayListLineProps<ListMcoStay> & {
  isQuickViewOpen?: boolean
}>(), {
  automationType: null,
  isQuickViewOpen: false
})

const {
  patientName,
  staySeen,
  stayStart,
  stayEnd,
  validationDate,
  moneyGap,
  displayedSeenStatus,
  searches,
  groupingError,
} = useStayListLine(props.stay)

const store = useStore()
const pmsiCountry = computed(() => store.state.settings.pmsi.country)

const firstValue = computed(() => formatCost(props.stay.firstStandardModeValue))
const currentValue = computed(() => {
  if (ModeEnum.AUTOMATION === props.mode && AutomationTypeEnum.AI === props.automationType) {
    return formatGroupingCost(
      (pmsiCountry.value === CountryEnum.FR ? props.stay.automationModeGhm : props.stay.automationModeDrg) !== null,
      props.stay.automationModeValue
    )
  }
  return formatGroupingCost(
    (pmsiCountry.value === CountryEnum.FR ? props.stay.standardModeGhm : props.stay.standardModeDrg) !== null,
    props.stay.standardModeValue
  )
})
const confidence = computed(() => formatConfidence(props.stay.confidence))

const { viewStay } = useStayRedirection(`/mco/id/${props.stay.id}`)
const { isQuickModeEnabled } = useQuickMode(computed(() => props.mode))

const emit = defineEmits<{
  (e: 'toggle-selected-stay', stayId: number)
  (e: 'open-quick-view', stay: ListMcoStay)
}>()
function toggleSelectedStay() {
  emit('toggle-selected-stay', props.stay.id)
}

// Quick mode: a click opens the aside instead of navigating (middle-click still navigates).
// stopPropagation only in this branch so switching asides doesn't let the click reach the
// outside-click directive and immediately close what was just (re)opened.
function onLineClick(event: MouseEvent) {
  if (isQuickModeEnabled.value) {
    event.stopPropagation()
    emit('open-quick-view', props.stay)
    return
  }
  viewStay(event)
}
</script>

<template>
  <tr
    :class="{
      'unseen-stay': staySeen.statusValue >= SeenStatusEnum.UNSEEN,
      'selected': selected,
      'quick-view-open': isQuickViewOpen
    }"
    name="stayList_line"
    class="stay-list-line text-center align-items-center"
    @click="onLineClick($event)"
    @click.middle="viewStay($event, true)"
  >
    <notification-line-column
      :displayed="'notification' in columns"
      :stay-seen="staySeen"
    />
    <td v-if="columns.rssId">
      {{ stay.rssId }}
    </td>
    <td v-if="columns.administrativeLocalStayId">
      {{ stay.administrativeLocalStayId }}
    </td>
    <td v-if="columns.patientName">
      {{ patientName }}
    </td>
    <td v-if="columns.firstGhm">
      <span>{{ stay.firstStandardModeGhm ? stay.firstStandardModeGhm.reference : '' }}</span>
    </td>
    <td v-if="columns.currentGhm">
      <span v-if="ModeEnum.AUTOMATION === mode && AutomationTypeEnum.AI === automationType">{{ stay.automationModeGhm ? stay.automationModeGhm.reference : '' }}</span>
      <span v-else>{{ stay.standardModeGhm ? stay.standardModeGhm.reference : '' }}</span>
    </td>
    <td v-if="columns.firstDrg">
      <span>{{ stay.firstStandardModeDrg ? stay.firstStandardModeDrg.reference : '' }}</span>
    </td>
    <td v-if="columns.currentDrg">
      <span v-if="ModeEnum.AUTOMATION === mode && AutomationTypeEnum.AI === automationType">{{ stay.automationModeDrg ? stay.automationModeDrg.reference : '' }}</span>
      <span v-else>{{ stay.standardModeDrg ? stay.standardModeDrg.reference : '' }}</span>
    </td>
    <td v-if="columns.firstGhmLevel">
      <span>{{ stay.firstStandardModeGhm ? stay.firstStandardModeGhm.level : '' }}</span>
    </td>
    <td v-if="columns.currentGhmLevel">
      <span v-if="ModeEnum.AUTOMATION === mode && AutomationTypeEnum.AI === automationType">{{ stay.automationModeGhm ? stay.automationModeGhm.level : '' }}</span>
      <span v-else>{{ stay.standardModeGhm ? stay.standardModeGhm.level : '' }}</span>
    </td>
    <td v-if="columns.firstValue">
      <span>{{ firstValue }}</span>
    </td>
    <td v-if="columns.currentValue">
      <span>{{ currentValue }}</span>
    </td>
    <td v-if="columns.moneyGap">
      <span>{{ moneyGap }}</span>
    </td>
    <td v-if="columns.rumCount">
      {{ stay.rumCount }}
    </td>
    <td v-if="columns.stayStart">
      {{ stayStart }}
    </td>
    <td v-if="columns.stayEnd">
      {{ stayEnd }}
    </td>
    <td v-if="columns.validationDate">
      {{ validationDate }}
    </td>
    <td v-if="columns.stayDuration">
      {{ stay.stayDuration }}
    </td>
    <td v-if="columns.confidence">
      {{ confidence }} %
    </td>
    <td v-if="columns.dpConfidence">
      {{ Math.round(stay.bestDPConfidence * 100) }} %
    </td>
    <td v-if="columns.countSavedSearches">
      {{ searches.length }}
    </td>
    <td
      v-if="columns.savedSearches"
      :title="searches.join('\n')"
      class="saved-searches-column"
    >
      {{ searches.join(' -- ') }}
    </td>
    <td v-if="columns.status">
      <span>{{ statusLabels[stay.status] ? statusLabels[stay.status] : stay.status }}</span>
    </td>
    <td v-if="columns.seen">
      {{ displayedSeenStatus }}
    </td>
    <td v-if="columns.externalPmsiStatus">
      <span>
        {{ ExternalPmsiStatusLabel[stay.externalPmsiStatus] || stay.externalPmsiStatus || '' }}
      </span>
    </td>
    <td v-if="columns.supervisingMajorDoctor">
      {{ stay.supervisingMajorDoctor }}
    </td>
    <td
      v-if="columns.practitionerName"
      :title="stay.csvPractitioners.replaceAll(', ', '\n')"
    >
      <span class="freetext-wrapper">{{ stay.csvPractitioners }}</span>
    </td>
    <td
      v-if="columns.comment"
      :title="stay.comment"
    >
      <span class="freetext-wrapper">{{ stay.comment }}</span>
    </td>
    <td v-if="columns.groupingError">
      {{ groupingError }}
    </td>
    <td v-if="columns.hasSancareNewCoding">
      {{ stay.hasSancareNewCoding ? 'Oui' : 'Non' }}
    </td>
    <td
      @click.stop=""
      @click.middle.stop=""
    >
      <input
        v-if="allowSelection"
        :checked="selected"
        type="checkbox"
        @change="toggleSelectedStay"
      >
    </td>
  </tr>
</template>
